import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Network,
  Users,
  Building2,
  Loader2,
  AlertCircle,
  CheckCircle,
  Star,
  Search,
  AlertTriangle,
} from 'lucide-react';
import { dbService, CDMOApplication, StartupSignup, IntroductionRequest } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
// import { DETAILED_CAPABILITIES_OPTIONS_MAP, PRIMARY_CAPABILITIES_OPTIONS, BATCH_SIZES_COMBINED_OPTIONS } from './CDMOApplication';
import CDMOCard from '../components/CDMOCard';
import FilterSidebar from '../components/FilterSidebar';
import {
  formatCloseAlternativeCount,
  formatEligibleCount,
  hasMoreResults,
  nextBatchSize,
  RESULTS_PER_PAGE,
  splitVisibleResults,
  staggerDelaySeconds,
} from '../utils/resultPagination';
import { PRIMARY_CAPABILITIES_OPTIONS, BATCH_SIZES_COMBINED_OPTIONS, DETAILED_CAPABILITIES_OPTIONS_MAP, BUDGET_RANGES_OPTIONS, getDetailedCapabilityOptions, getPrimaryCapabilityLabel, OPERATING_REGIONS_OPTIONS, CDMO_BUSINESS_PROFILE_OPTIONS, GMP_CERTIFICATIONS_OPTIONS, PROJECT_TYPES_OPTIONS } from '../constants';
import {
  defaultMatchingWeights,
  getTier2ScoringWeights,
  MatchingWeights,
  TIER2_CUSTOMIZABLE_WEIGHT_KEYS,
} from '../lib/matching';
import {
  computeCDMOMatchScore,
  BASE_SCORE_WEIGHTS,
  type BaseScoreWeights,
  type ScoringRequest,
} from '../lib/scoringEngine';
import { initializeSynonyms } from '../lib/synonyms';
import {
  passesTier1Gatekeeper,
  hasActiveGatekeeperFilters,
  getGatekeeperTooltipBreakdownRows,
  isTier1PartnerTypeFilterActive,
  cdmoMatchesCompanySearchQuery,
  buildCloseAlternativeRelaxedConstraints,
  type GatekeeperFilterState,
  type CloseAlternativeRelaxedConstraint,
} from '../lib/gatekeeperFilters';
import ReviewShareOverlay from '../components/ReviewShareOverlay';
import ExpertGuidanceFeature from '../components/ExpertGuidanceFeature';
import {
  DEFAULT_SHARED_FIELD_SETTINGS,
  SharedFieldSettings,
  buildSharedProjectBrief,
} from '../lib/warmIntroSharing';

// User matching weights interface (local definition to avoid conflict)
interface LocalUserMatchingWeights {
  user_id: string;
  company_name?: number;
  description?: number;
  facility_locations_details?: number;
  primary_capabilities?: number;
  gmp_certifications?: number;
  preferred_project_types?: number;
  engagement_scales?: number;
  typical_batch_sizes?: number;
  target_client_types?: number;
  detailed_capabilities?: number;
  facility_locations?: number;
  speed_to_start?: number;
  created_at?: string;
  updated_at?: string;
}

// Enhanced score breakdown interface
interface ScoreBreakdown {
  productType: { earned: number; max: number; details?: string };
  detailedServices: { earned: number; max: number; details?: string };
  developmentStage: { earned: number; max: number; details?: string };
  supportType: { earned: number; max: number; details?: string };
  batchSize: { earned: number; max: number; details?: string };
  budget: { earned: number; max: number; details?: string };
  partnerType: { earned: number; max: number; details?: string };
  certifications: { earned: number; max: number; details?: string };
  operating_regions: { earned: number; max: number; details?: string };
  speedToStart: { earned: number; max: number; details?: string };
  startupFriendly: { earned: number; max: number; details?: string };
  otherDetails: { earned: number; max: number; details?: string };
  cdmoPartnerProfile: { earned: number; max: number; details?: string };
}
interface EnhancedMatchResult {
  score: number;
  maxPossibleScore: number;
  percentage: number;
  /** Unrounded percentage used for ordering, so fractional differences still separate CDMOs. */
  rawPercentage: number;
  /** False when every active dimension has its slider at zero — nothing left to score. */
  hasWeightedDimensions: boolean;
  breakdown: ScoreBreakdown;
}

interface ScoredCDMO extends CDMOApplication {
  matchScore: number;
  maxPossibleScore: number;
  percentageMatch?: number;
  rankingScore: number;
  scoreBreakdown: ScoreBreakdown;
  /** All Tier-1 mismatches vs strict filters (partner + certifications may both apply). */
  relaxedConstraints?: CloseAlternativeRelaxedConstraint[];
}

interface SectionedResults {
  fallbackTriggered: boolean;
  perfectMatches: ScoredCDMO[];
  closeAlternatives: ScoredCDMO[];
}

interface PendingIntroductionRequest {
  cdmoId: string;
  cdmoName: string;
}

/** Static filter option lists for the matching sidebar (stable reference for memoization). */
const STATIC_FILTER_OPTIONS = {
  productTypeOptions: PRIMARY_CAPABILITIES_OPTIONS,
  developmentStageOptions: [
    'Preclinical R&D',
    'Phase I',
    'Phase II',
    'Phase III',
    'Commercial Manufacturing',
    'Tech Transfer',
  ],
  // Shared with the CDMO intake rather than duplicated: both sides must offer the same values for
  // the support-services comparison to work, and a hand-copied list is how they drift apart.
  supportTypeOptions: PROJECT_TYPES_OPTIONS,
  batchSizeOptions: [...BATCH_SIZES_COMBINED_OPTIONS],
  budgetOptions: [...BUDGET_RANGES_OPTIONS, 'Not sure / flexible'],
  cdmoPartnerTypeOptions: [...CDMO_BUSINESS_PROFILE_OPTIONS],
  certificationOptions: [...GMP_CERTIFICATIONS_OPTIONS],
  locationOptions: [...OPERATING_REGIONS_OPTIONS, 'No preference'],
  speedToStartOptions: ['Within 1 month', '1–3 months', '3–6 months', 'Flexible / Planning stage'],
};

const MatchingPlatformPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [cdmoData, setCdmoData] = useState<CDMOApplication[]>([]);
  const [startupData, setStartupData] = useState<StartupSignup[]>([]);
  const [userRequests, setUserRequests] = useState<IntroductionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestingIntroduction, setRequestingIntroduction] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [showRequestError, setShowRequestError] = useState(false);
  const [matchingMode, setMatchingMode] = useState<'system' | 'custom'>('system');
  const [, setUserWeights] = useState<LocalUserMatchingWeights | null>(null);
  const [customWeights, setCustomWeights] = useState<MatchingWeights>(defaultMatchingWeights);
  const [lastSavedCustomWeights, setLastSavedCustomWeights] = useState<MatchingWeights>(defaultMatchingWeights);
  const [baseScoreWeights, setBaseScoreWeights] = useState<BaseScoreWeights>(BASE_SCORE_WEIGHTS);
  const [userCompanyName, setUserCompanyName] = useState('');
  const [userCompanyStage, setUserCompanyStage] = useState('');
  const [pendingIntroductionRequest, setPendingIntroductionRequest] = useState<PendingIntroductionRequest | null>(null);
  const [sharedFieldSettings, setSharedFieldSettings] = useState<SharedFieldSettings>(DEFAULT_SHARED_FIELD_SETTINGS);
  /**
   * §7 — how many of the eligible results are currently revealed. Ranking is computed over the
   * whole eligible set, so revealing more never reorders or recomputes anything; it only draws
   * further down a list that already exists.
   */
  const [visibleCount, setVisibleCount] = useState(RESULTS_PER_PAGE);

  useEffect(() => {
    if (!showRequestError) return;
    const timeout = window.setTimeout(() => setShowRequestError(false), 5000);
    return () => window.clearTimeout(timeout);
  }, [showRequestError]);
  // Configurable result limit

  // New dynamic filter state based on user-facing questions
  const [filters, setFilters] = useState({
    search: '',
    // Step 1: What are you developing?
    productType: [] as string[],
    // Step 2: What do you need help with? (Dynamic based on Step 1)
    detailedServices: [] as string[],
    // Step 3: What stage are you in?
    developmentStage: '',
    // Step 4: What kind of support do you need?
    supportType: [] as string[],
    // Step 5: What batch size do you expect to need?
    batchSize: '',
    // Step 6: What is your  project  budget?
    budget: '',
    // CDMO Business Profile (Tier 1) — admin-set `cdmo_business_profile` on each CDMO row
    cdmoPartnerType: '',
    // Step 8: Certifications Required
    certifications: [] as string[],
    // Step 9: Preferred CDMO location(s)
    location: [] as string[],
    // Step 10: Do you need the CDMO to start quickly?
    speedToStart: '',
  });

  //fetch synonyms from the database
  useEffect(() => {
    initializeSynonyms();
  }, []);

  // Fetch user matching weights
  useEffect(() => {
    const fetchUserWeights = async () => {
      if (!user?.id) return;

      try {
        const weights = await dbService.getUserMatchingWeights(user.id);
        if (weights) {
          const loaded = { ...defaultMatchingWeights };
          for (const key of TIER2_CUSTOMIZABLE_WEIGHT_KEYS) {
            const v = weights[key];
            if (v !== undefined && v !== null) {
              loaded[key] = v;
            }
          }
          // console.log('Loaded user weights:', loaded);
          setUserWeights(weights);
          setCustomWeights(loaded);
          setLastSavedCustomWeights(loaded);
          setMatchingMode('custom');
        }
      } catch (error) {
        console.error('Error fetching user weights:', error);
      }
    };

    fetchUserWeights();
  }, [user?.id]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;

      try {
        const profile = await dbService.getUserProfile(user.id);
        setUserCompanyName(profile?.company || '');
        const stages = profile?.development_stage;
        setUserCompanyStage(Array.isArray(stages) ? stages.join(', ') : (stages || ''));
      } catch (profileError) {
        console.error('Error fetching user profile for intro sharing:', profileError);
      }
    };

    fetchUserProfile();
  }, [user?.id]);

  // Load base score weights from DB so scoring can be tuned without code changes.
  useEffect(() => {
    const fetchScoringBaseWeights = async () => {
      try {
        const dbWeights = await dbService.getScoringBaseWeights();
        setBaseScoreWeights((prev) => ({
          ...prev,
          ...dbWeights,
        }));
      } catch (err) {
        console.error('Error fetching scoring base weights:', err);
      }
    };

    fetchScoringBaseWeights();
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const promises = [
          dbService.getAllCDMOApplications(),
          dbService.getAllStartupSignups()
        ];

        // If user is authenticated, also fetch their introduction requests
        if (user?.id) {
          promises.push(dbService.getUserIntroductionRequests(user.id));
        }

        const results = await Promise.all(promises);
        const [cdmos, startups, requests] = results;

        setCdmoData(cdmos);
        setStartupData(startups);
        if (requests) {
          setUserRequests(requests);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // Available options for filters
  const availableOptions = useMemo(() => {
    return {
      ...STATIC_FILTER_OPTIONS,
      detailedCapabilitiesMap: DETAILED_CAPABILITIES_OPTIONS_MAP,
    };
  }, []);

  /** Tier 1 gatekeeper slice (binary pass/fail before ranking). */
  const gatekeeperFilters: GatekeeperFilterState = useMemo(
    () => ({
      productType: filters.productType,
      detailedServices: filters.detailedServices,
      cdmoPartnerType: filters.cdmoPartnerType,
      certifications: filters.certifications,
    }),
    [filters.productType, filters.detailedServices, filters.cdmoPartnerType, filters.certifications]
  );

  /**
   * Tier 1 technical feasibility: every active gatekeeper criterion is ANDed; failures are excluded
   * before Tier 2 weighted scores (`calculateMatchScore` below). Same contract as
   * `validateTechnicalFeasibility` in `gatekeeperFilters`.
   */
  const cdmosAfterTier1Gate = useMemo(
    () => cdmoData.filter((cdmo) => passesTier1Gatekeeper(cdmo, gatekeeperFilters)),
    [cdmoData, gatekeeperFilters]
  );

  /** Tier 1 pass + company search substring (not scored, not a hard gate). */
  const cdmosAfterCompanySearch = useMemo(
    () =>
      cdmosAfterTier1Gate.filter((cdmo) => cdmoMatchesCompanySearchQuery(cdmo, filters.search)),
    [cdmosAfterTier1Gate, filters.search]
  );


  const sectionedResults = useMemo<SectionedResults>(() => {
    const speedSel = filters.speedToStart.trim();
    const tier2FiltersActive =
      !!filters.developmentStage?.trim() ||
      filters.supportType.length > 0 ||
      !!filters.batchSize?.trim() ||
      (Boolean(filters.budget?.trim()) && filters.budget !== 'Not sure / flexible') ||
      (filters.location.length > 0 && !filters.location.includes('No preference')) ||
      speedSel.length > 0;

    const calculateMatchScore = (cdmo: CDMOApplication): EnhancedMatchResult => {

      // Soft (Tier 2) ranking inputs only — gatekeeper fields do not add scoring dimensions.
      const isBudgetActive = Boolean(filters.budget?.trim()) && filters.budget !== 'Not sure / flexible';
      const isLocationActive = filters.location.length > 0 && !filters.location.includes('No preference');
      const isDevelopmentStageActive = Boolean(filters.developmentStage?.trim());
      const isSupportTypeActive = filters.supportType.length > 0;
      const isBatchSizeActive = Boolean(filters.batchSize?.trim());
      const isSpeedToStartActive = speedSel.length > 0;

      const tier2RankingInputsActive =
        isDevelopmentStageActive ||
        isSupportTypeActive ||
        isBatchSizeActive ||
        isBudgetActive ||
        isLocationActive ||
        isSpeedToStartActive;

      // Map sidebar speed-to-start labels → canonical SPEED_TO_START_ORDER values
      const speedLabelMap: Record<string, string> = {
        'Within 1 month': 'Immediate (within 2 weeks)',
        '1–3 months': 'Standard (1-2 months)',
        '1-3 months': 'Standard (1-2 months)',
        '3–6 months': 'Extended (2-3 months)',
        '3-6 months': 'Extended (2-3 months)',
        'Flexible / Planning stage': 'Project Dependent',
      };

      // Map sidebar development-stage labels → canonical DEVELOPMENT_STAGE_ORDER values
      const stageLabelMap: Record<string, string> = {
        'Preclinical R&D':          'Preclinical R&D',
        'Phase I':                  'Phase I Clinical',
        'Phase II':                 'Phase II Clinical',
        'Phase III':                'Phase III Clinical',
        'Commercial Manufacturing': 'Commercial Manufacturing',
        'Tech Transfer':            'Tech Transfer Support',
      };

      // Build the ScoringRequest from current sidebar filter state
      const scoringRequest: ScoringRequest = {
        batchSize: filters.batchSize?.trim() || undefined,
        developmentStage: filters.developmentStage
          ? (stageLabelMap[filters.developmentStage] ?? filters.developmentStage)
          : undefined,
        speedToStart: speedSel ? (speedLabelMap[speedSel] ?? speedSel) : undefined,
        budget: isBudgetActive ? filters.budget : undefined,
        operatingRegions: isLocationActive ? filters.location : undefined,
        supportServices: isSupportTypeActive ? filters.supportType : undefined,
      };

      const tier2Weights = getTier2ScoringWeights(
        matchingMode === 'custom' ? customWeights : undefined
      );

      const matchScore = computeCDMOMatchScore(
        cdmo,
        scoringRequest,
        tier2Weights,
        baseScoreWeights
      );

      const bd = matchScore?.breakdown;

      /**
       * Tooltip shows weighted points (Excel-style): base earned/max × Tier-2 slider for each row.
       */
      const w = (active: boolean, mult: number, earned: number, max: number) =>
        active ? { earned: earned * mult, max: max * mult } : { earned: 0, max: 0 };

      // Map engine breakdown → ScoreBreakdown (full-precision; UI rounds on display)
      const breakdown: ScoreBreakdown = {
        productType:       { earned: 0, max: 0, details: '' },
        detailedServices:  { earned: 0, max: 0, details: '' },
        partnerType:       { earned: 0, max: 0, details: '' },
        certifications:    { earned: 0, max: 0, details: '' },
        startupFriendly:   { earned: 0, max: 0, details: '' },
        otherDetails:      { earned: 0, max: 0, details: '' },
        cdmoPartnerProfile:{ earned: 0, max: 0, details: '' },
        developmentStage: {
          ...w(
            isDevelopmentStageActive,
            tier2Weights.engagement_scales,
            bd?.development_stage?.earned ?? 0,
            bd?.development_stage?.max ?? 0
          ),
          details: bd?.development_stage?.rationale ?? '',
        },
        supportType: {
          ...w(
            isSupportTypeActive,
            tier2Weights.preferred_project_types,
            bd?.support_services?.earned ?? 0,
            bd?.support_services?.max ?? 0
          ),
          details: bd?.support_services?.rationale ?? '',
        },
        batchSize: {
          ...w(
            isBatchSizeActive,
            tier2Weights.typical_batch_sizes,
            bd?.batch_size?.earned ?? 0,
            bd?.batch_size?.max ?? 0
          ),
          details: bd?.batch_size?.rationale ?? '',
        },
        budget: {
          ...w(isBudgetActive, tier2Weights.budget, bd?.budget?.earned ?? 0, bd?.budget?.max ?? 0),
          details: bd?.budget?.rationale ?? '',
        },
        operating_regions: {
          ...w(
            isLocationActive,
            tier2Weights.operating_regions,
            bd?.geography?.earned ?? 0,
            bd?.geography?.max ?? 0
          ),
          details: bd?.geography?.rationale ?? '',
        },
        speedToStart: {
          ...w(
            isSpeedToStartActive,
            tier2Weights.speed_to_start,
            bd?.speed_to_start?.earned ?? 0,
            bd?.speed_to_start?.max ?? 0
          ),
          details: bd?.speed_to_start?.rationale ?? '',
        },
      };

      const gatekeeperFiltersSlice: GatekeeperFilterState = {
        productType: filters.productType,
        detailedServices: filters.detailedServices,
        cdmoPartnerType: filters.cdmoPartnerType,
        certifications: filters.certifications,
      };

      // When no Tier 2 dimensions are active, show Tier 1 gatekeeper rows in the tooltip.
      if (
        !tier2RankingInputsActive &&
        hasActiveGatekeeperFilters(gatekeeperFiltersSlice)
      ) {
        Object.assign(breakdown, getGatekeeperTooltipBreakdownRows(gatekeeperFiltersSlice));
        // Relabels only the displayed sentence — gatekeeperFilters.ts itself keeps comparing the
        // raw stored modality strings, untouched.
        if (breakdown.productType) {
          breakdown.productType = {
            ...breakdown.productType,
            details: `Lists all selected modalities: ${filters.productType.map(getPrimaryCapabilityLabel).join(', ')}`,
          };
        }
      }

      // Derive the percentage:
      //   - If tier2 inputs are active → use the engine's 0-100 score directly.
      //   - If only gatekeeper criteria are active → every CDMO that passed Tier 1 shows 100%.
      //   - If nothing is active → undefined (handled upstream).
      // Only show percentage for Tier 2 scoring logic.
      // Tier 1 (hard filters) is pass/fail only and should not display a score.
      const percentage = tier2RankingInputsActive ? (matchScore?.percentageScore ?? 0) : 0;

      const finalPercentage = percentage;

      return {
        score: matchScore?.totalScore ?? 0,
        maxPossibleScore: 100,
        percentage: finalPercentage,
        rawPercentage: tier2RankingInputsActive ? (matchScore?.rawPercentageScore ?? 0) : 0,
        hasWeightedDimensions: (matchScore?.activeWeightTotal ?? 0) > 0,
        breakdown,
      };
    };

    const budgetFilterActive =
      Boolean(filters.budget?.trim()) && filters.budget !== 'Not sure / flexible';
    const locationFilterActive =
      filters.location.length > 0 && !filters.location.includes('No preference');

    const hasActiveFilters =
      Boolean(filters.search?.trim()) ||
      filters.productType.length > 0 ||
      filters.detailedServices.length > 0 ||
      Boolean(filters.developmentStage?.trim()) ||
      filters.supportType.length > 0 ||
      Boolean(filters.batchSize?.trim()) ||
      budgetFilterActive ||
      isTier1PartnerTypeFilterActive(filters.cdmoPartnerType) ||
      filters.certifications.length > 0 ||
      locationFilterActive ||
      speedSel.length > 0;

    const scoreAndRank = (
      list: CDMOApplication[],
      relaxedConstraintsForCdmo?: (cdmo: CDMOApplication) => CloseAlternativeRelaxedConstraint[] | undefined
    ): ScoredCDMO[] => {
      const scored = list.map((cdmo) => {
        const matchData = calculateMatchScore(cdmo);
        return {
          ...cdmo,
          matchScore: matchData.score,
          maxPossibleScore: matchData.maxPossibleScore,
          percentageMatch:
            tier2FiltersActive && matchData.hasWeightedDimensions ? matchData.percentage : undefined,
          rankingScore:
            tier2FiltersActive && matchData.hasWeightedDimensions ? matchData.rawPercentage : 0,
          scoreBreakdown: matchData.breakdown,
          relaxedConstraints: relaxedConstraintsForCdmo?.(cdmo),
        };
      });

      if (!hasActiveFilters) {
        return scored.map((cdmo) => ({ ...cdmo, percentageMatch: undefined }));
      }

      /**
       * Score decides ordering, never eligibility. Everything reaching here has already cleared
       * `passesTier1Gatekeeper`, so it passes the sponsor's hard stops by definition; a zero on
       * the weighted dimensions ranks it last rather than removing it. Dropping those rows would
       * put hard-stop-passing partners outside both the eligible count and Load More's reach —
       * PHAR-404: "the eligible count matches the true eligible set, not just what's loaded".
       */
      return scored.sort((a, b) => b.rankingScore - a.rankingScore);
    };

    /**
     * Scope §3/§8/§9: Project Kickoff timing is a SOFT weighted factor. A CDMO slower than the
     * requested window must score lower via scoreSpeedToStart, never be dropped from the pool —
     * so no timing predicate narrows this list.
     */
    const perfectMatches = scoreAndRank(cdmosAfterCompanySearch);
    /**
     * Fallback when the user-facing list is sparse — measured against the same pool as the grid
     * (Tier 1 + company search), so it now fires on what it was always about: hard stops narrow
     * enough that relaxing one is the only way to show the sponsor anything.
     */
    const shouldRunFallback =
      hasActiveGatekeeperFilters(gatekeeperFilters) && perfectMatches.length <= 2;

    if (!shouldRunFallback) {
      return {
        fallbackTriggered: false,
        perfectMatches,
        closeAlternatives: [],
      };
    }

    // Determine which hard constraints are actually active to establish proper relaxation order
    const isPartnerTypeFilterActive = isTier1PartnerTypeFilterActive(gatekeeperFilters.cdmoPartnerType);
    const isCertificationsFilterActive = gatekeeperFilters.certifications.length > 0;

    // First relaxation level: only relax Partner Type (if it was actually active)
    const relaxedPartnerTypeFilters: GatekeeperFilterState = {
      ...gatekeeperFilters,
      ...(isPartnerTypeFilterActive && { cdmoPartnerType: '' }),
    };

    // Second relaxation level: relax Partner Type (if active) AND Certifications (if active)
    const relaxedCertificationFilters: GatekeeperFilterState = {
      ...gatekeeperFilters,
      ...(isPartnerTypeFilterActive && { cdmoPartnerType: '' }),
      ...(isCertificationsFilterActive && { certifications: [] }),
    };

    const perfectIds = new Set(cdmosAfterTier1Gate.map((cdmo) => cdmo.id).filter(Boolean));
    const alternativesById = new Map<string, CDMOApplication>();

    // Apply first relaxation: relax Partner Type constraint (if it was active)
    if (isPartnerTypeFilterActive) {
      const partnerRelaxedPool = cdmoData.filter(
        (cdmo) =>
          passesTier1Gatekeeper(cdmo, relaxedPartnerTypeFilters) &&
          cdmoMatchesCompanySearchQuery(cdmo, filters.search)
      );
      for (const cdmo of partnerRelaxedPool) {
        if (!cdmo.id || perfectIds.has(cdmo.id)) continue;
        if (!alternativesById.has(cdmo.id)) {
          alternativesById.set(cdmo.id, cdmo);
        }
      }
    }

    // Apply second relaxation: relax Regulatory Certifications constraint (if it was active)
    if (isCertificationsFilterActive) {
      const certificationRelaxedPool = cdmoData.filter(
        (cdmo) =>
          passesTier1Gatekeeper(cdmo, relaxedCertificationFilters) &&
          cdmoMatchesCompanySearchQuery(cdmo, filters.search)
      );
      for (const cdmo of certificationRelaxedPool) {
        if (!cdmo.id || perfectIds.has(cdmo.id)) continue;
        if (!alternativesById.has(cdmo.id)) {
          alternativesById.set(cdmo.id, cdmo);
        }
      }
    }

    const closeAlternatives = scoreAndRank(Array.from(alternativesById.values()), (cdmo) => {
      const labels = buildCloseAlternativeRelaxedConstraints(cdmo, gatekeeperFilters);
      return labels.length > 0 ? labels : undefined;
    });

    return {
      fallbackTriggered: true,
      perfectMatches,
      closeAlternatives,
    };
  }, [
    cdmoData,
    cdmosAfterTier1Gate,
    cdmosAfterCompanySearch,
    filters,
    matchingMode,
    customWeights,
    baseScoreWeights,
    gatekeeperFilters,
  ]);

  const filteredCDMOs = useMemo(
    () =>
      sectionedResults.fallbackTriggered
        ? [...sectionedResults.perfectMatches, ...sectionedResults.closeAlternatives]
        : sectionedResults.perfectMatches,
    [sectionedResults]
  );

  /**
   * §7 — the eligible total: CDMOs that actually pass the sponsor's hard-stop criteria, which is
   * exactly the Perfect Matches. Close Alternatives are deliberately excluded, because they only
   * surface by *relaxing* those criteria, so counting them as eligible would overstate the match.
   * With a narrow search that is the difference between "2 eligible" and "90".
   */
  const totalEligible = sectionedResults.perfectMatches.length;

  /** Everything reachable by paging, including the relaxed-criteria group. */
  const totalRevealable =
    sectionedResults.perfectMatches.length + sectionedResults.closeAlternatives.length;

  const { visiblePerfectMatches, visibleCloseAlternatives } = useMemo(
    () =>
      splitVisibleResults(
        sectionedResults.perfectMatches,
        sectionedResults.closeAlternatives,
        visibleCount
      ),
    [sectionedResults, visibleCount]
  );

  /** The flat (non-fallback) grid shows the same reveal budget. */
  const visibleCDMOs = useMemo(
    () => filteredCDMOs.slice(0, visibleCount),
    [filteredCDMOs, visibleCount]
  );

  const canLoadMore = hasMoreResults(totalRevealable, visibleCount);

  /**
   * Collapse back to the first batch whenever the sponsor changes what they asked for. Ranking is
   * recomputed for new criteria, so holding a stale reveal depth would show results from a list
   * the sponsor never scrolled through.
   *
   * Adjusted during render rather than in an effect. An effect runs after the commit, so a sponsor
   * who had paged deep would get one painted frame of the *new* ranking at the *old* depth — 150
   * cards mounting with layout animations and then unmounting again, which is both the freeze this
   * scope set out to remove and a glimpse of exactly what the paragraph above forbids.
   */
  const criteriaSignature = JSON.stringify([filters, matchingMode, customWeights]);
  const [lastCriteriaSignature, setLastCriteriaSignature] = useState(criteriaSignature);
  if (criteriaSignature !== lastCriteriaSignature) {
    setLastCriteriaSignature(criteriaSignature);
    setVisibleCount(RESULTS_PER_PAGE);
  }

  const loadMoreStatusRef = useRef<HTMLParagraphElement>(null);
  /** True only while the sponsor's own activation is what exhausted the list. */
  const loadMoreExhaustedByUser = useRef(false);

  const handleLoadMore = () => {
    const nextCount = visibleCount + RESULTS_PER_PAGE;
    loadMoreExhaustedByUser.current = !hasMoreResults(totalRevealable, nextCount);
    setVisibleCount(nextCount);
  };

  /**
   * The activation that reveals the last result also removes the button the sponsor is standing
   * on, so focus would fall back to <body> and the next Tab would restart at the top of the page —
   * losing their place in a list they were deliberately paging through. Hand focus to the status
   * line instead, which is also what gets the completion read out.
   */
  useEffect(() => {
    if (canLoadMore || !loadMoreExhaustedByUser.current) return;
    loadMoreExhaustedByUser.current = false;
    loadMoreStatusRef.current?.focus();
  }, [canLoadMore]);

  const fallbackRequirementText = useMemo(() => {
    const productTypes = filters.productType.length > 0 ? filters.productType.map(getPrimaryCapabilityLabel).join(', ') : 'your selected product type';
    const services = filters.detailedServices.length > 0 ? filters.detailedServices.join(', ') : 'your selected specific services';
    const certifications =
      filters.certifications.length > 0
        ? filters.certifications.join(', ')
        : 'your selected certifications';

    return { productTypes, services, certifications };
  }, [filters.productType, filters.detailedServices, filters.certifications]);

  // Check if we should show the "closest matches" message
  const showClosestMatchMessage = useMemo(() => {
    const hasActiveFilters =
      Boolean(filters.search?.trim()) ||
      filters.productType.length > 0 ||
      filters.detailedServices.length > 0 ||
      Boolean(filters.developmentStage?.trim()) ||
      filters.supportType.length > 0 ||
      Boolean(filters.batchSize?.trim()) ||
      Boolean(filters.budget?.trim()) ||
      isTier1PartnerTypeFilterActive(filters.cdmoPartnerType) ||
      filters.certifications.length > 0 ||
      filters.location.length > 0 ||
      Boolean(filters.speedToStart?.trim());

    if (!hasActiveFilters || filteredCDMOs.length === 0 || sectionedResults.fallbackTriggered) {
      return false;
    }

    const topMatch = filteredCDMOs[0];
    return topMatch && topMatch.percentageMatch !== undefined && topMatch.percentageMatch < 80;
  }, [filteredCDMOs, filters, sectionedResults.fallbackTriggered]);

  const hasMatchingFiltersActive = useMemo(() => {
    const budgetFilterActive =
      Boolean(filters.budget?.trim()) && filters.budget !== 'Not sure / flexible';
    const locationFilterActive =
      filters.location.length > 0 && !filters.location.includes('No preference');

    return (
      Boolean(filters.search?.trim()) ||
      filters.productType.length > 0 ||
      filters.detailedServices.length > 0 ||
      Boolean(filters.developmentStage?.trim()) ||
      filters.supportType.length > 0 ||
      Boolean(filters.batchSize?.trim()) ||
      budgetFilterActive ||
      isTier1PartnerTypeFilterActive(filters.cdmoPartnerType) ||
      filters.certifications.length > 0 ||
      locationFilterActive ||
      Boolean(filters.speedToStart?.trim())
    );
  }, [filters]);

  type FilterValue =
    | string
    | string[]
    | undefined;

  const handleFilterChange = (filterType: string, value: FilterValue) => {
    if (filterType === 'productType' && Array.isArray(value)) {
      const newProductTypes = value as string[];
      const allowedServices = new Set(
        newProductTypes.flatMap((pt) => getDetailedCapabilityOptions(pt))
      );
      setFilters((prev) => ({
        ...prev,
        productType: newProductTypes,
        detailedServices: prev.detailedServices.filter((s) => allowedServices.has(s)),
      }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      productType: [],
      detailedServices: [],
      developmentStage: '',
      supportType: [],
      batchSize: '',
      budget: '',
      cdmoPartnerType: '',
      certifications: [],
      location: [],
      speedToStart: '',
    });
  };

  // Handle introduction request
  const submitIntroductionRequest = async (
    cdmoId: string,
    settings: SharedFieldSettings
  ) => {
    if (!user?.id) {
      console.error('Please sign in to request an introduction.');
      return;
    }

    try {
      setRequestingIntroduction(cdmoId);

      // Get user's name from user metadata or profile
      const userName = user.user_metadata?.name || user.email?.split('@')[0] || 'Unknown User';
      const sharedProjectBrief = buildSharedProjectBrief(
        filters,
        settings,
        userCompanyName,
        userCompanyStage
      );

      const requestData: IntroductionRequest = {
        requester_user_id: user.id,
        requester_name: userName,
        requester_email: user.email || '',
        cdmo_id: cdmoId,
        request_details: filters,
        shared_field_settings: settings,
        shared_project_brief: sharedProjectBrief,
        status: 'pending'
      };

      const savedRequest = await dbService.createIntroductionRequest(requestData);

      // Merge the authoritative row (accurate id/status/created_at — whether newly
      // created, or the pre-existing one returned when a duplicate active request
      // already exists for this sponsor+CDMO pair) onto the submitted data, instead
      // of fabricating a placeholder id/timestamp locally. Dedupe against the
      // already-loaded list in case this resolved to an existing request.
      setUserRequests(prev => {
        if (prev.some(r => r.id === savedRequest.id)) return prev;
        return [...prev, { ...requestData, ...savedRequest }];
      });

      // Show custom success message
      setShowSuccessMessage(true);
      setShowRequestError(false);

      // Auto-hide after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

    } catch (error) {
      console.error('Error requesting introduction:', error);
      setShowRequestError(true);
    } finally {
      setRequestingIntroduction(null);
    }
  };

  const handleRequestIntroduction = (cdmoId: string) => {
    const targetCDMO = cdmoData.find((cdmo) => cdmo.id === cdmoId);
    setSharedFieldSettings(DEFAULT_SHARED_FIELD_SETTINGS);
    setPendingIntroductionRequest({
      cdmoId,
      cdmoName: targetCDMO?.company_name || 'Selected CDMO',
    });
  };

  const handleConfirmReviewSend = async () => {
    if (!pendingIntroductionRequest) return;

    await submitIntroductionRequest(
      pendingIntroductionRequest.cdmoId,
      sharedFieldSettings
    );
    setPendingIntroductionRequest(null);
  };

  // Check if user has already requested introduction to a specific CDMO
  const hasRequestedIntroduction = (cdmoId: string) => {
    return userRequests.some(request => request.cdmo_id === cdmoId);
  };

  // Get introduction request for a specific CDMO
  const getIntroductionRequestForCDMO = (cdmoId: string) => {
    return userRequests.find(request => request.cdmo_id === cdmoId);
  };

  // Handle matching mode change
  const handleMatchingModeChange = (mode: 'system' | 'custom') => {
    setMatchingMode(mode);
    if (mode === 'custom') {
      setCustomWeights(lastSavedCustomWeights);
    }
  };

  // Handle weight change
  const handleWeightChange = (weightType: keyof MatchingWeights, value: number) => {
    setCustomWeights(prev => ({
      ...prev,
      [weightType]: value
    }));
  };

  // Save custom weights
  const saveCustomWeights = async () => {
    if (!user?.id) return;

    try {
      await dbService.saveUserMatchingWeights(user.id, customWeights);
      setLastSavedCustomWeights(customWeights);
      setShowSaveMessage(true);
      setTimeout(() => setShowSaveMessage(false), 3000);
    } catch (error) {
      console.error('Error saving custom weights:', error);
    }
  };

  // Reset to default weights
  const resetToDefaultWeights = async () => {
    if (!user?.id) return;

    try {
      await dbService.resetUserMatchingWeights(user.id);
      setCustomWeights(defaultMatchingWeights);
      setLastSavedCustomWeights(defaultMatchingWeights);
      setMatchingMode('custom');
      setUserWeights(null);
    } catch (error) {
      console.error('Error resetting weights:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-charcoal mb-2">Loading CDMO Network</h2>
          <p className="text-slate-gray">Fetching manufacturing partners...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center border border-white/50 max-w-md"
        >
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-charcoal mb-2">Error Loading Data</h2>
          <p className="text-slate-gray mb-4">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Header */}
      <div className="hero bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30 max-md:max-h-none md:max-h-[40vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-4 sm:pt-5 sm:pb-4">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex items-start w-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-primary rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg shrink-0 mt-1">
                <Network className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-2xl font-bold text-charcoal leading-snug">
                  Send Intro Requests to Top CDMOs — For free. We handle the rest.
                </h1>

                <p className="text-sm sm:text-base text-slate-gray mt-1 leading-snug">
                  Request unlimited introductions. We manage outreach, communication, and follow-ups — completely free.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 sm:space-x-6 text-sm shrink-0 lg:pt-1">
              <div className="flex items-center">
                <Building2 className="w-4 h-4 text-electric-blue mr-1.5 sm:mr-2" />
                <span className="text-charcoal font-medium">{cdmoData.length} CDMOs</span>
              </div>
              <div className="flex items-center">
                <Users className="w-4 h-4 text-lime-green mr-1.5 sm:mr-2" />
                <span className="text-charcoal font-medium">{startupData.length} Companies</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message Overlay */}
      <AnimatePresence>
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSuccessMessage(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-white/50"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-16 h-16 bg-gradient-to-r from-lime-green to-soft-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
              >
                <CheckCircle className="w-8 h-8 text-white" />
              </motion.div>

              <h3 className="text-2xl font-bold text-charcoal mb-4">Introduction Request Sent!</h3>
              <p className="text-slate-gray mb-6 leading-relaxed">
                Warm intro request sent. The CDMO will receive only the project-fit fields you approved.
              </p>

              <div className="bg-electric-blue/10 border border-electric-blue/30 rounded-xl p-4 mb-6">
                <p className="text-electric-blue text-sm font-medium">
                  💡 What happens next?
                </p>
                <p className="text-slate-gray text-sm mt-1">
                  The CDMO will review your approved project-fit details and respond within 2-3 business days.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSuccessMessage(false)}
                className="w-full px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Got It!
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRequestError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="fixed top-4 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 px-4"
          >
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-lg">
              Something went wrong while sending your intro request. Please try again.
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {user?.id && (
          <ExpertGuidanceFeature
            sponsorName={user.user_metadata?.username || ''}
            sponsorEmail={user.email || ''}
            sponsorCompany={userCompanyName}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filter Sidebar */}
          <div className="lg:col-span-1">
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              availableOptions={availableOptions}
              matchingMode={matchingMode}
              customWeights={customWeights}
              onMatchingModeChange={handleMatchingModeChange}
              onWeightChange={handleWeightChange}
              onSaveWeights={saveCustomWeights}
              onResetWeights={resetToDefaultWeights}
              isAuthenticated={!!user}
            />
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-bold text-charcoal">
                    {hasMatchingFiltersActive ? 'Matched CDMO Results' : 'Manufacturing Partners'}
                    {/* §7 — the total available, not the number currently on screen. "Eligible"
                        only means something once criteria have been set, so browsing keeps the
                        plainer wording. */}
                    <span className="ml-2 text-sm font-normal text-slate-gray">
                      ({hasMatchingFiltersActive
                        ? formatEligibleCount(totalEligible)
                        : `${totalEligible} result${totalEligible === 1 ? '' : 's'}`}
                      {sectionedResults.closeAlternatives.length > 0 &&
                        ` + ${formatCloseAlternativeCount(sectionedResults.closeAlternatives.length)}`}
                      )
                    </span>
                  </h2>
                  {hasMatchingFiltersActive && (
                    <p className="text-sm text-slate-gray mt-1">
                      Matches adjust based on your selected project criteria.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {filteredCDMOs.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-12 text-center"
              >
                <Building2 className="w-16 h-16 text-slate-gray/50 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-charcoal mb-2">No CDMOs Found</h3>
                <p className="text-slate-gray mb-4">
                  No manufacturing partners match your current filters. Try adjusting your criteria.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClearFilters}
                  className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Clear All Filters
                </motion.button>
              </motion.div>
            ) : (
              <>
                {sectionedResults.fallbackTriggered && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl"
                  >
                    <p className="text-yellow-900 font-semibold text-sm">Limited Results Found</p>
                    <p className="text-yellow-800 text-sm mt-1">
                      Your search criteria are very specific and only a small number of CDMOs match all requirements.
                    </p>
                    <p className="text-yellow-800 text-sm">
                      You may improve results by adjusting the project requirements
                    </p>
                  </motion.div>
                )}

                {sectionedResults.fallbackTriggered ? (
                  <div className="space-y-8">
                    {visiblePerfectMatches.length > 0 && (
                      <section>
                        <h3 className="text-lg font-bold text-charcoal flex items-center gap-2.5">
                          <Star
                            className="h-5 w-5 shrink-0 text-amber-500 fill-amber-500 drop-shadow-sm"
                            strokeWidth={1.5}
                            aria-hidden
                          />
                          <span>Perfect Matches</span>
                        </h3>
                        <p className="text-sm text-slate-gray mt-1 mb-4">
                          {`These partners meet 100% of your critical requirements for ${fallbackRequirementText.productTypes}, ${fallbackRequirementText.services}, and ${fallbackRequirementText.certifications}.`}
                        </p>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                          <AnimatePresence mode="popLayout">
                            {visiblePerfectMatches.map((cdmo, index) => (
                              <motion.div
                                key={cdmo.id}
                                layout
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 30 }}
                                transition={{ duration: 0.3, delay: staggerDelaySeconds(index) }}
                              >
                                <CDMOCard
                                  cdmo={cdmo}
                                  percentageMatch={cdmo.percentageMatch}
                                  onRequestIntroduction={handleRequestIntroduction}
                                  isRequestDisabled={
                                    hasRequestedIntroduction(cdmo.id || '') ||
                                    requestingIntroduction === cdmo.id
                                  }
                                  requestStatus={getIntroductionRequestForCDMO(cdmo.id || '')?.status || ''}
                                  introductionRequestId={getIntroductionRequestForCDMO(cdmo.id || '')?.id || ''}
                                  isAdmin={isAdmin}
                                />
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      </section>
                    )}

                    {visibleCloseAlternatives.length > 0 && (
                      <section>
                        <h3 className="text-lg font-bold text-charcoal flex items-center gap-2.5">
                          <Search
                            className="h-5 w-5 shrink-0 text-slate-gray"
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span>Close Alternatives</span>
                        </h3>
                        <p className="text-sm text-slate-gray mt-1 mb-4">
                          These highly capable partners match your core technical needs but differ slightly in other areas.
                        </p>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                          <AnimatePresence mode="popLayout">
                            {visibleCloseAlternatives.map((cdmo, index) => (
                              <motion.div
                                key={cdmo.id}
                                layout
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 30 }}
                                transition={{ duration: 0.3, delay: staggerDelaySeconds(index) }}
                              >
                                {cdmo.relaxedConstraints && cdmo.relaxedConstraints.length > 0 && (
                                  <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2">
                                    <AlertTriangle
                                      className="h-4 w-4 shrink-0 text-amber-600 mt-0.5"
                                      strokeWidth={2}
                                      aria-hidden
                                    />
                                    <div className="flex min-w-0 flex-col gap-1">
                                      {cdmo.relaxedConstraints.map((rc, idx) => (
                                        <span key={`${rc.key}-${idx}`}>
                                          {rc.key}: {rc.value}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <CDMOCard
                                  cdmo={cdmo}
                                  percentageMatch={cdmo.percentageMatch}
                                  scoreDisplayMode="strong_alternative"
                                  onRequestIntroduction={handleRequestIntroduction}
                                  isRequestDisabled={
                                    hasRequestedIntroduction(cdmo.id || '') ||
                                    requestingIntroduction === cdmo.id
                                  }
                                  requestStatus={getIntroductionRequestForCDMO(cdmo.id || '')?.status || ''}
                                  introductionRequestId={getIntroductionRequestForCDMO(cdmo.id || '')?.id || ''}
                                  isAdmin={isAdmin}
                                />
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      </section>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <AnimatePresence mode="popLayout">
                      {visibleCDMOs?.map((cdmo, index) => (
                        <motion.div
                          key={cdmo.id}
                          layout
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 30 }}
                          transition={{
                            duration: 0.3,
                            delay: staggerDelaySeconds(index)
                          }}
                        >
                          <CDMOCard
                            cdmo={cdmo}
                            percentageMatch={cdmo.percentageMatch}
                            onRequestIntroduction={handleRequestIntroduction}
                            isRequestDisabled={
                              hasRequestedIntroduction(cdmo.id || "") ||
                              requestingIntroduction === cdmo.id
                            }
                            requestStatus={getIntroductionRequestForCDMO(cdmo.id || "")?.status || ""}
                            introductionRequestId={getIntroductionRequestForCDMO(cdmo.id || "")?.id || ""}
                            isAdmin={isAdmin}
                          />
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}

                {/* §7 — reveals the next batch in the established order. Purely a view change:
                    the ranked list behind it is not recomputed, so nothing reorders. */}
                {totalRevealable > RESULTS_PER_PAGE && (
                  <div className="mt-8 flex flex-col items-center gap-2">
                    {canLoadMore && (
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleLoadMore}
                        aria-describedby="load-more-status"
                        className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                      >
                        Load More Matches
                      </motion.button>
                    )}
                    {/* Outlives the button deliberately. The last activation removes the control,
                        and a status region that went with it could neither announce that the list
                        was complete nor give the focus standing on that button anywhere to land.
                        It also spells the split out rather than leaving the sponsor to reconcile
                        this total against the two numbers in the heading. */}
                    <p
                      ref={loadMoreStatusRef}
                      id="load-more-status"
                      tabIndex={-1}
                      className="text-sm text-slate-gray focus:outline-none"
                      aria-live="polite"
                    >
                      {canLoadMore
                        ? `Showing ${Math.min(visibleCount, totalRevealable)} of ${totalRevealable}`
                        : `Showing all ${totalRevealable}`}
                      {sectionedResults.closeAlternatives.length > 0 &&
                        ` (${totalEligible} eligible, ${formatCloseAlternativeCount(sectionedResults.closeAlternatives.length)})`}
                      {canLoadMore && ` — next ${nextBatchSize(totalRevealable, visibleCount)}`}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          {/* Closest Match Message */}
          {showClosestMatchMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-electric-blue/10 border border-electric-blue/30 rounded-xl"
            >
              <p className="text-electric-blue text-sm font-medium">
                💡 Here are the closest CDMO matches based on your needs. Some may not meet all requirements, but could still be a strong fit.
              </p>
            </motion.div>
          )}

        </div>
      </div>

      {showSaveMessage && (
        <div className="fixed top-4 right-4 z-50 bg-lime-100 border border-lime-400 text-lime-900 px-4 py-2 rounded shadow">
          Your customized weights saved.
        </div>
      )}

      <ReviewShareOverlay
        isOpen={!!pendingIntroductionRequest}
        cdmoName={pendingIntroductionRequest?.cdmoName || ''}
        filters={filters}
        sponsorCompanyName={userCompanyName}
        companyStage={userCompanyStage}
        settings={sharedFieldSettings}
        sending={!!requestingIntroduction}
        onToggle={(key, checked) =>
          setSharedFieldSettings((prev) => ({
            ...prev,
            [key]: checked,
          }))
        }
        onCancel={() => setPendingIntroductionRequest(null)}
        onSend={handleConfirmReviewSend}
        onSendAnyway={handleConfirmReviewSend}
      />
    </div>
  );
};


export default MatchingPlatformPage;
